import type {
  BürgschaftstemplateAllgemein as BuergschaftstemplateAllgemein,
  Historieneintrag,
  HistorieneintragAGT,
} from '@/types/Dokument';
import { SharedDokumentService } from '@/shared/api/services/dokument/dokument.ts';
import type { Bürgschaftsart as Buergschaftsart } from '@/types/Bürgschaft';
import type { BürgschaftstemplateÖffentlich as BuergschaftstemplateOeffentlich } from '@/types/DokumentOEF';
import { configForDownload, configForMultipartFormData, toJsonBlob } from '@/connection/base';
import type { BürgschaftstemplateBürgentext as BuergschaftstemplateBuergentext } from '@/types/DokumentBGT';
import type { AuftraggebertextPruefungErgebnis } from '@/shared/Dokumente/types';
import type { AxiosResponse } from 'axios';
import type { Dokument } from '@/types/DokumentUpload.ts';
import type { Entscheidungsergebnis } from '@/types/DokumentAGT.ts';
import type { Person } from '@/types/Partner.ts';

export class DokumentService extends SharedDokumentService {
  protected dokumentServiceBaseUrlBg = `${this.dokumentServiceBaseUrl}/buerge`;

  async holebuergschaftstemplate(
    auftraggeberId: string,
    buergschaftsart: string,
    vertragsdatum: string,
  ): Promise<BuergschaftstemplateAllgemein> {
    return this.httpClient.get(`${this.dokumentServiceBaseUrlBg}/buergschaftstemplate`, {
      params: {
        auftraggeberId,
        buergschaftsart,
        vertragsdatum,
      },
    });
  }

  async holeTemplatesNachId(id: string): Promise<BuergschaftstemplateAllgemein> {
    return this.httpClient.get(`${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/${id}`);
  }

  async holeOeffentlicheTemplates(
    buergschaftsart: Buergschaftsart,
  ): Promise<BuergschaftstemplateOeffentlich[]> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/öffentlich`,
      {
        params: {
          buergschaftsart,
        },
      },
    );
  }

  async holeBuergentemplates(
    buergschaftsart: Buergschaftsart,
  ): Promise<BuergschaftstemplateBuergentext[]> {
    return this.httpClient.get(`${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/bürgen`, {
      params: {
        buergschaftsart,
      },
    });
  }

  async holeTemplatePdf(
    buergschaftstemplateId: string,
    baurecht?: string,
    befristung?: boolean,
  ): Promise<Blob> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/auftraggebertext_${buergschaftstemplateId}.pdf`,
      {
        params: {
          befristung,
          recht: baurecht,
        },
        config: configForDownload,
      },
    );
  }

  async holeGescannteDatei(handle: string): Promise<Blob> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftsvorlagen/upload/${handle}`,
      { config: configForDownload },
    );
  }

  async holeBuergschaftPdfOrginal(buergschaftsId: number): Promise<Blob> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftsdokumente/buergschaft_${buergschaftsId}.pdf`,
      { config: configForDownload },
    );
  }

  async holeTemplateBuergenPruefergebnisse(
    templateId: string,
  ): Promise<AuftraggebertextPruefungErgebnis> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/${templateId}/pruefungsergebnis`,
    );
  }

  async sendBuergschaftstemplateVerwendet(id: string, beguenstigterId: string) {
    this.httpClient.post(`${this.dokumentServiceBaseUrl}/buergschaftstemplates/${id}/verwendet`, {
      data: {
        beguenstigterId,
      },
    });
  }

  async isBuergschaftstemplateAktiv(
    id: string,
    auftraggeberId: string,
    vertragsdatum: string,
  ): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/${id}/aktiv`,
      {
        params: {
          auftraggeberId,
          vertragsdatum,
        },
      },
    );
  }

  async isBuergschaftDokumentVorhanden(buergschaftId: number): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftsdokumente/${buergschaftId}/vorhanden`,
    );
  }

  async findNichtPruefbareTemplateIds(): Promise<string[]> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/nicht-pruefbare-ids`,
    );
  }

  async manuellerUploadBuergschaftsdokument(
    dokument: Dokument,
    auftraggeberId: string,
    auftragnehmerId: string,
    buergschaftId: number,
    buergschaftIdBuerge: string,
    token?: string,
  ): Promise<AxiosResponse<void>> {
    const formData = new FormData();

    const buergschaftsdaten = {
      auftraggeberId,
      auftragnehmerId,
      bürgschaftId: buergschaftId,
      bürgschaftIdBürge: buergschaftIdBuerge,
      token,
      dokument,
    };

    formData.append('buergschaftsdaten', toJsonBlob(buergschaftsdaten));

    return this.httpClient.post(`${this.dokumentServiceBaseUrlBg}/buergschaftsdokument/upload`, {
      data: formData,
      config: configForMultipartFormData,
    });
  }

  async downloadAuftragsvorlage(id: string | number): Promise<Blob> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftsauftrag/${id}/vorlage`,
      { config: configForDownload },
    );
  }

  async downloadBeschlussdokumentation(buergschaftstemplateId: string): Promise<Blob> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftsdokumente/pruefbericht/${buergschaftstemplateId}.pdf`,
      { config: configForDownload },
    );
  }

  async erledigungErstpruefung(
    buergschaftstemplateId: string,
    internerKommentar: string,
    externerKommentar: string,
    entscheidungsergebnis: Entscheidungsergebnis,
    buergschaftstemplateVersion: number,
  ): Promise<void> {
    const templateDaten = {
      internerKommentar,
      externerKommentar,
      entscheidungsergebnis,
      bürgschaftstemplateVersion: buergschaftstemplateVersion,
    };
    return this.httpClient.post(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/${buergschaftstemplateId}/erstprüfung`,
      { data: templateDaten },
    );
  }

  async erledigungZweitpruefung(
    buergschaftstemplateId: string,
    internerKommentar: string,
    externerKommentar: string,
    entscheidungsergebnis: Entscheidungsergebnis,
    ansprechpartner: Person,
    buergschaftstemplateVersion: number,
  ): Promise<void> {
    const templateDaten = {
      internerKommentar,
      externerKommentar,
      entscheidungsergebnis,
      ansprechpartner,
      bürgschaftstemplateVersion: buergschaftstemplateVersion,
    };
    return this.httpClient.post(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/${buergschaftstemplateId}/zweitprüfung`,
      { data: templateDaten },
    );
  }

  async holeTemplateHistorieAGT(id: string): Promise<HistorieneintragAGT[]> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/${id}/historie`,
    );
  }

  async holeTemplateHistorie(id: string): Promise<Historieneintrag[]> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/${id}/historie`,
    );
  }

  async erstprueferNichtZweitpruefer(templateId: string): Promise<boolean> {
    return this.httpClient.get(
      `${this.dokumentServiceBaseUrlBg}/buergschaftstemplates/${templateId}/istnutzererstpruefer`,
    );
  }
}
